import { EmploymentIncome, Income, SocialSecurityIncome } from "src/types";
import { moyrToAnnual, printNumber, splitDate } from "../utils";
import { calculateEmploymentIncome } from "./employment-income";
import { CalculationInfo } from "./types";
import {
  NRA,
  adjustForIncrease,
  adjustForInflation,
  birthday,
  isDead,
  noNote,
  retirementYear,
  ssPercent,
} from "./utils";
import { startEndToYear } from "src/components/Info/StartEndSelect";

const startYear = new Date().getFullYear();
export const calculateSocialSecurity = (
  info: CalculationInfo<SocialSecurityIncome>,
) => {
  const { income, people, currentYear } = info;
  const incomes = info.scenario.incomes.filter(
    (i: Income) => i.enabled,
  ) as Income[];
  const person = people[income.personId];
  const { birthYear } = birthday(person);
  const age = currentYear - birthYear;
  let ownAmount = 0;

  if (isDead(info, income.personId)) return noNote(0);

  //find spousal
  //
  const spouse = incomes.find(
    (i) => i.type === "social-security" && i.personId === 1 - income.personId,
  ) as SocialSecurityIncome;
  if (spouse && isDead(info, spouse.personId)) {
    // actually taking survivor benefit
    if (age >= 60) {
      const newInfo = {
        ...info,
        income: spouse,
      };
      ownAmount = Math.max(ownAmount, calculateSurvivorSocialSecurity(newInfo));
    }
  }

  const startAgeYear =
    startEndToYear(income.start, info.people, info.scenario) - birthYear;

  if (income.start.type !== "already-receiving" && startAgeYear > age) {
    const amount = reduceByIncome(info, ownAmount);
    if (amount.result != ownAmount)
      return {
        note: `Due to employment income of ${printNumber(amount.income)} your 
Social Security has been reduced from ${printNumber(ownAmount)}
to ${printNumber(amount.result)} for this year`,
        amount: amount.result,
      };
    else return { note: "", amount: amount.result };
  }

  // if retired
  ownAmount = Math.max(calculateOwnSocialSecurity(info), ownAmount);

  if (spouse) {
    // calculate spouse SS todo
    const spouseId = spouse.personId;
    // const { birthYear: spouseBirthYear } = birthday(people[spouseId]);
    if (
      !isDead(info, spouseId) &&
      startEndToYear(spouse.start, info.people, info.scenario) <= currentYear
    ) {
      const newInfo = {
        ...info,
        income: spouse,
      } as CalculationInfo<SocialSecurityIncome>;

      const startAgeMonth = income.startAgeMonth || 1; //
      const prorate = age === startAgeYear ? (12 - startAgeMonth + 1) / 12 : 1;
      const spousal = calculateOwnSocialSecurity(newInfo) / 2;
      ownAmount = Math.max(ownAmount, spousal * prorate);
    }
  }

  const amount = reduceByIncome(info, ownAmount);
  if (amount.result != ownAmount)
    return {
      note: `Due to employment income of ${printNumber(amount.income)} your 
Social Security has been reduced from ${printNumber(ownAmount)}
to ${printNumber(amount.result)} for this year`,
      amount: amount.result,
    };
  else {
    return noNote(amount.result);
  }
};

export const calculateOwnSocialSecurity = (
  info: CalculationInfo<SocialSecurityIncome>,
) => {
  const { income, people, currentYear } = info;

  const person = people[income.personId];
  const { year: birthYear } = splitDate(person.birthday);
  let startAgeMonth = 0;
  let startAgeYear = 2025;
  if (income.start.type === "fra") {
    const [ny, nm] = NRA(birthYear);
    startAgeYear = ny;
    startAgeMonth = nm + 1;
  } else {
    startAgeYear =
      startEndToYear(income.start, info.people, info.scenario) - birthYear;
    startAgeMonth = income.startAgeMonth || 1; //
  }
  const age = currentYear - birthYear;
  let ownAmount = 0;

  if (income.calculationMethod == "manual") {
    const amount = moyrToAnnual(
      income.amount
        ? income.amount
        : { value: income.annualAmount, type: "yearly" },
    );
    if (income.start.type === "already-receiving") {
      ownAmount = amount;
    } else if (startAgeYear == age) {
      ownAmount = (amount * (12 - (startAgeMonth || 1) + 1)) / 12;
    } else if (startAgeYear < age) {
      ownAmount = amount;
    }
  } else {
    const prorate = age === startAgeYear ? (12 - startAgeMonth + 1) / 12 : 1;
    ownAmount =
      (income.pia *
        12 *
        prorate *
        ssPercent(person.birthday, startAgeYear, startAgeMonth)) /
      100;
  }
  if (income.start.type !== "already-receiving" && startAgeYear > age) return 0;

  // calculate reduction
  // const years =
  //   info.currentYear -
  //   (income.alreadyReceiving
  //     ? info.startYear
  //     : income.startAgeYear + birthYear);
  //
  ownAmount = adjustForIncrease(
    info,
    ownAmount,
    Math.max(birthYear + startAgeYear, startYear),
    // income.startAgeYear + birthYear,
  );
  ownAmount = adjustForInflation(
    info,
    ownAmount,

    Math.max(birthYear + startAgeYear, startYear),
  );
  return ownAmount;
};

export const calculateSurvivorSocialSecurity = (
  info: CalculationInfo<SocialSecurityIncome>,
) => {
  const { income, people } = info;
  const deathYears = info.scenario.deathYears;
  const person = people[income.personId];
  const { year: birthYear } = splitDate(person.birthday);
  const deathYear = deathYears[income.personId];

  const startAgeYear =
    startEndToYear(income.start, info.people, info.scenario) - birthYear;
  let ownAmount = 0;
  if (deathYear - birthYear < startAgeYear) {
    const newInfo = {
      ...info,
      currentYear: deathYear + birthYear,
    };
    ownAmount = calculateOwnSocialSecurity(newInfo);
  } else {
    const newInfo = {
      ...info,
      currentYear: deathYear + birthYear,
    };
    ownAmount = calculateOwnSocialSecurity(newInfo);
  }

  const years = deathYear + birthYear;
  // const y = Math.max(birthYear + income.startAgeYear, info.startYear);

  ownAmount = adjustForIncrease(info, ownAmount, years);
  // ownAmount = adjustCompoundInterest(ownAmount, years, income.cola);

  ownAmount = adjustForInflation(info, ownAmount, years);
  return ownAmount;
};

const reduceByIncome = (
  info: CalculationInfo<SocialSecurityIncome>,
  ownAmount: number,
) => {
  const { income, currentYear } = info;

  const incomes = info.scenario.incomes.filter(
    (i: Income) => i.enabled,
  ) as Income[];
  const person = info.people[income.personId];
  const employment = incomes.find(
    (i) => i.type === "employment-income" && i.personId === income.personId,
  ) as EmploymentIncome | undefined;

  if (employment) {
    const newInfo: CalculationInfo<EmploymentIncome> = {
      ...info,
      income: employment,
    };

    const a = calculateEmploymentIncome(newInfo).amount;
    const r = retirementYear(person.birthday);
    if (currentYear < r && a > 23400) {
      ownAmount = Math.max(ownAmount - (a - 23400) / 2, 0);
    }
    if (currentYear == r && a > 62160) {
      ownAmount = Math.max(ownAmount - (a - 62160) / 3, 0);
    }
    return { result: ownAmount, income: a };
  }
  return { result: ownAmount, income: 0 };
};
